import { Component, OnInit, OnDestroy } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { RateLimitUiService } from '../../core/services/rate-limit-ui.service';
import { Subscription, Observable, Subject, BehaviorSubject } from 'rxjs';
import { map, startWith, switchMap, takeUntil, filter, tap, distinctUntilChanged } from 'rxjs/operators';
import { interval } from 'rxjs/observable/interval';
import { of } from 'rxjs/observable/of';
import { timer } from 'rxjs/observable/timer';

@Component({
  selector: 'app-rate-limit-indicator',
  template: `
    <div class="rate-limit-indicator" *ngIf="status$ | async as status">
      <div *ngIf="shouldShowIndicator(status)" 
           [@slideInOut]
           [class.critical]="status.remainingPercentage < 20"
           [class.warning]="status.remainingPercentage < 50 && status.remainingPercentage >= 20"
           class="container">
        
        <div class="header">
          <div class="status-title">
            Maximum Search Limit Reached
          </div>
          <button class="close-button" (click)="dismiss()">
            <i class="fa fa-times"></i>
          </button>
        </div>
        
        <div class="content">
          <!-- Main countdown timer -->
          <div class="countdown-wrapper">
            <div class="countdown-timer">
              <div class="timer-value">{{ timeLeftValue || 0 }}</div>
              <div class="timer-label">sec</div>
            </div>
            <div class="progress-ring">
              <svg width="120" height="120" viewBox="0 0 120 120">
                <circle 
                  cx="60" 
                  cy="60" 
                  r="54"
                  fill="none"
                  stroke="#e9ecef"
                  stroke-width="12"
                />
                <circle 
                  cx="60" 
                  cy="60" 
                  r="54"
                  fill="none"
                  [attr.stroke]="getColorForPercentage(status.remainingPercentage)"
                  stroke-width="12"
                  stroke-dasharray="339.292"
                  [attr.stroke-dashoffset]="339.292 * (1 - countdownProgressValue)"
                  transform="rotate(-90, 60, 60)"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .rate-limit-indicator {
      position: fixed;
      bottom: 20px;
      right: 20px;
      z-index: 1000;
      max-width: 400px;
      width: 100%;
    }
    
    .container {
      border-radius: 12px;
      box-shadow: 0 6px 20px rgba(0,0,0,0.2);
      background: #fff;
      padding: 16px;
      overflow: hidden;
      transition: all 0.3s ease;
    }
    
    .container.warning {
      border-top: 4px solid #ffc107;
    }
    
    .container.critical {
      border-top: 4px solid #dc3545;
    }
    
    .header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 16px;
    }
    
    .status-title {
      font-weight: 600;
      font-size: 16px;
      color: #212529;
    }
    
    .content {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }
    
    .countdown-wrapper {
      display: flex;
      justify-content: center;
      align-items: center;
      position: relative;
    }
    
    .countdown-timer {
      position: absolute;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      z-index: 2;
    }
    
    .timer-value {
      font-size: 28px;
      font-weight: 700;
      line-height: 1;
    }
    
    .timer-label {
      font-size: 12px;
      color: #6c757d;
      margin-top: 4px;
    }
    
    .progress-ring {
      position: relative;
    }
    
    .status-info {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }
    
    .info-item {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 14px;
      color: #495057;
    }
    
    .info-item i {
      color: #6c757d;
      font-size: 14px;
      width: 16px;
      text-align: center;
    }
    
    .usage-bar {
      margin-top: 4px;
    }
    
    .usage-label {
      font-size: 12px;
      color: #6c757d;
      margin-bottom: 4px;
    }
    
    .close-button {
      background: none;
      border: none;
      cursor: pointer;
      font-size: 14px;
      color: #6c757d;
      padding: 4px;
    }
    
    .close-button:hover {
      color: #343a40;
    }
  `],
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ transform: 'translateY(20px)', opacity: 0 }),
        animate('300ms ease-out', style({ transform: 'translateY(0)', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('300ms ease-in', style({ transform: 'translateY(20px)', opacity: 0 }))
      ])
    ])
  ]
})
export class RateLimitIndicatorComponent implements OnInit, OnDestroy {
  status$ = this.rateLimitUi.getStatus();
  timeLeft$: Observable<number>;
  
  // Use regular properties instead of async pipes for these values
  timeLeftValue = 0;
  countdownProgressValue = 0;
  
  private subscription: Subscription = new Subscription();
  private destroy$ = new Subject<void>();
  private isFirstLoad = true;
  private hasAutoClosed = false;
  private lastLogTime = 0;
  private autoDismissTime = 0; // Track when we auto-dismissed
  private readonly COOLDOWN_PERIOD = 5000; // 5 seconds cooling off period
  private readonly AUTO_CLOSE_TIMEOUT = 30000; // 30 seconds default auto-close
  private autoCloseSubscription: Subscription;
  
  constructor(private rateLimitUi: RateLimitUiService) {
    console.log('RATE-LIMIT: Component instantiated');
  }
  
  ngOnInit() {
    console.log('RATE-LIMIT: Component initializing');
    this.setupCountdownTimer();
    // Don't set up additional auto-close timer - we'll rely on the countdown
  }
  
  ngOnDestroy() {
    console.log('RATE-LIMIT: Component destroying');
    this.destroy$.next();
    this.destroy$.complete();
    this.subscription.unsubscribe();
  }
  
  // Throttle logging to prevent browser freeze
  logIfNeeded(message: string) {
    const now = Date.now();
    // Only log once per second
    if (now - this.lastLogTime > 1000) {
      console.log(message);
      this.lastLogTime = now;
    }
  }
  
  setupCountdownTimer() {
    console.log('RATE-LIMIT: Setting up countdown timer');
    // Create a countdown that updates every second
    const countdownTick$ = interval(1000).pipe(takeUntil(this.destroy$));
    
    this.timeLeft$ = this.status$.pipe(
      // Ignore new statuses during cooldown period after auto-dismiss
      filter(status => {
        const now = Date.now();
        const inCooldownPeriod = now - this.autoDismissTime < this.COOLDOWN_PERIOD;
        
        if (inCooldownPeriod && !status.dismissed) {
          console.log('RATE-LIMIT: Ignoring status update during cooldown period');
          // Auto-dismiss any status updates during cooldown
          setTimeout(() => this.rateLimitUi.dismissStatus(), 0);
          return false;
        }
        return true;
      }),
      // Prevent re-processing when already dismissed
      filter(status => !status.dismissed),
      // Only process when status changes significantly
      distinctUntilChanged((prev, curr) => {
        return prev.resetTime === curr.resetTime && 
               prev.resetTimeoutSeconds === curr.resetTimeoutSeconds &&
               prev.dismissed === curr.dismissed;
      }),
      switchMap(status => {
        console.log('RATE-LIMIT: New status received', {
          resetTime: status.resetTime,
          resetSeconds: status.resetTimeoutSeconds,
          dismissed: status.dismissed
        });
        
        // Reset auto-close flag for new status updates
        this.hasAutoClosed = false;
        
        // Start a new auto-close timer based on the status's reset timeout
        this.setupAutoClose(status.resetTimeoutSeconds || 30);
        
        // If we have explicit reset seconds, use that directly
        if (status.resetTimeoutSeconds && status.resetTimeoutSeconds > 0) {
          console.log('RATE-LIMIT: Using explicit timeout:', status.resetTimeoutSeconds);
          let remainingSeconds = status.resetTimeoutSeconds;
          
          return countdownTick$.pipe(
            startWith(0), // Emit immediately
            map(() => {
              // Decrease the remaining time for each tick
              remainingSeconds = Math.max(0, remainingSeconds - 1);
              return remainingSeconds;
            })
          );
        }
        
        // Fall back to date-based countdown if we have a resetTime
        if (status.resetTime) {
          const now = new Date().getTime();
          const resetTimeMs = status.resetTime.getTime();
          
          // If resetTime is not in the future, use a minimum display time
          if (resetTimeMs <= now) {
            console.log('RATE-LIMIT: Past reset time, using default');
            return of(30); // Force 30 seconds display time for better visibility
          }
          
          return countdownTick$.pipe(
            startWith(0), // Emit immediately
            map(() => {
              const currentTime = new Date().getTime();
              const diff = Math.max(0, resetTimeMs - currentTime);
              const secondsLeft = Math.ceil(diff / 1000);
              return secondsLeft;
            })
          );
        }
        
        // Default fallback
        console.log('RATE-LIMIT: No reset info, using default');
        // Create a countdown from 30 that actually decrements
        let defaultSeconds = 30;
        return countdownTick$.pipe(
          startWith(0),
          map(() => {
            defaultSeconds = Math.max(0, defaultSeconds - 1);
            return defaultSeconds;
          })
        );
      })
    );
    
    // Subscribe to timeLeft$ and update our property
    this.subscription.add(
      this.timeLeft$.subscribe(seconds => {
        // Update the property for template binding
        this.timeLeftValue = seconds;
        
        // Calculate progress
        const maxResetTime = 30;
        this.countdownProgressValue = Math.max(0, Math.min(1, 1 - (seconds / maxResetTime)));
        
        // Auto-close when countdown reaches zero - use a static flag to prevent multiple triggers
        if (seconds === 0 && !this.isFirstLoad && !this.hasAutoClosed) {
          console.log('RATE-LIMIT: Time reached zero, auto-closing');
          // Set flag to prevent multiple auto-close attempts
          this.hasAutoClosed = true;
          this.autoDismissTime = Date.now(); // Record the time we auto-dismissed
          // Use a small delay to ensure the UI updates with "0" before closing
          setTimeout(() => this.dismiss(), 500);
        }
        
        // Clear first load flag
        if (this.isFirstLoad) {
          this.isFirstLoad = false;
        }
      })
    );
  }
  
  // Updated to take seconds parameter and cancel previous timers
  setupAutoClose(seconds: number = 30) {
    console.log(`RATE-LIMIT: Setting up auto-close timer for ${seconds} seconds`);
    
    // Clear any existing auto-close timer
    if (this.autoCloseSubscription) {
      this.autoCloseSubscription.unsubscribe();
    }
    
    // Add a safety buffer to the timer (2 seconds) to allow the countdown to finish first
    const safetySeconds = seconds + 2;
    
    // Create a new auto-close subscription
    this.autoCloseSubscription = timer(safetySeconds * 1000)
      .pipe(
        takeUntil(this.destroy$),
        // Only trigger if not already auto-closed
        filter(() => !this.hasAutoClosed)
      )
      .subscribe(() => {
        console.log('RATE-LIMIT: Safety auto-close timer expired');
        this.hasAutoClosed = true;
        this.autoDismissTime = Date.now();
        this.dismiss();
      });
    
    // Add to main subscription for cleanup
    this.subscription.add(this.autoCloseSubscription);
  }
  
  getProgressBarType(percentage: number): string {
    if (percentage < 20) return 'danger';
    if (percentage < 50) return 'warning';
    return 'success';
  }
  
  getColorForPercentage(percentage: number): string {
    if (percentage < 20) return '#dc3545';
    if (percentage < 50) return '#ffc107';
    return '#28a745';
  }
  
  shouldShowIndicator(status: any): boolean {
    if (!status) return false;
    
    // Check if we're in the cooldown period after auto-dismissal
    const now = Date.now();
    if (now - this.autoDismissTime < this.COOLDOWN_PERIOD && !status.dismissed) {
      // Force dismiss during cooldown period
      setTimeout(() => this.rateLimitUi.dismissStatus(), 0);
      return false;
    }
    
    return (status.remainingPercentage < 50 || status.isThrottled) && !status.dismissed;
  }
  
  dismiss() {
    const currentStatus = this.rateLimitUi.getCurrentStatus();
    
    // Don't try to dismiss if already dismissed
    if (currentStatus && currentStatus.dismissed) {
      console.log('RATE-LIMIT: Already dismissed, ignoring dismiss call');
      return;
    }
    
    console.log('RATE-LIMIT: User dismissed indicator');
    this.rateLimitUi.dismissStatus();
  }
}


// WEBPACK FOOTER //
// ./src/app/shared/components/rate-limit-indicator.component.ts